import type { AtomExportInterface } from '@/app/editor/iframe/atom/atom-interface/AtomExportInterface';
import { GpChildProduct } from '@/app/editor/iframe/atom/module/GpChildProduct';
import type { AtomModel, FormControlSetting, Font } from '@/app/editor/iframe/atom/Atom.model';
import HELPER from '@/app/utils/HELPER';
import pSwatchesInfor from '@/app/editor/sidebar/module/pSwatches';
import GpChildProductExportService from '@/app/editor/iframe/atom/atom-service/export/GpChildProductExportService';

const FormControlSettings: Array<FormControlSetting> = [
  {
    type: 'css',
    name: 'label-bottom',
    default_value: '5px',
    targetSelector: 'label',
    property: 'padding-bottom',
    require: true,
    selfSelector: '-1,1', // Dùng $self cho `saveFormControls`: 1 không sử dụng cho `readDataFormControls`: -1
  },
  {
    type: 'css',
    name: 'label-font-size',
    default_value: '16px',
    targetSelector: 'label',
    property: 'font-size',
    require: true,
    selfSelector: '-1,1',
  },
  {
    type: 'css',
    name: 'label-line-height',
    default_value: '1.2em',
    targetSelector: 'label',
    property: 'line-height',
    unit: 'em',
    require: true,
    selfSelector: '-1,1',
  },
  {
    type: 'css',
    name: 'label-color',
    default_value: '#2d313f',
    targetSelector: 'label',
    property: 'color',
    fieldType: 'color',
    require: true,
    selfSelector: '-1,1',
  },
  {
    type: 'css',
    name: 'label-font',
    default_value: '',
    targetSelector: 'label',
    property: 'font-family',
    require: true,
    selfSelector: '-1,1',
  },
  {
    type: 'css:notSelector',
    name: 'font-size',
    property: 'font-size',
    default_value: '16px',
    require: true,
    selfSelector: '1,1',
  },
  {
    type: 'css:notSelector',
    name: 'color',
    property: 'color',
    fieldType: 'color',
    default_value: '#2d313f',
    require: true,
    selfSelector: '1,1',
  },
  {
    type: 'css:notSelector',
    name: 'font',
    property: 'font-family',
    default_value: '',
    require: true,
    selfSelector: '1,1',
  },
  {
    type: 'css:boxShadow',
    name: 'active-outline',
    targetSelector: '.gf_swatch.gf_selected',
    default_value: 1,
    property: 'box-shadow',
    shadow: {
      color: 'active-outline-color',
      spread: 'active-outline-size',
    },
    isBoxShadowGroup: true,
    activePanelSelector: '#active-outline',
    isSelf: true,
  },
  {
    type: 'css:boxShadow',
    name: 'active-outline-color',
    targetSelector: '.gf_swatch.gf_selected',
    default_value: '#000000',
    property: 'box-shadow',
    fieldType: 'color',
    activePanelSelector: '#active-outline',
    isSelf: true,
  },
  {
    type: 'css:boxShadow',
    name: 'active-outline-size',
    targetSelector: '.gf_swatch.gf_selected',
    default_value: '1px',
    property: 'box-shadow',
    fieldType: 'spread',
    activePanelSelector: '#active-outline',
    isSelf: true,
  },
  {
    type: 'css:notSelector',
    name: 'width',
    default_value: 'auto',
    property: 'width',
    selfSelector: '1,1',
  },
  {
    type: 'css:notSelector',
    name: 'height',
    default_value: '44px',
    property: 'height',
    require: true,
    selfSelector: '1,1',
  },
  {
    type: 'html:extraClass',
    name: 'extra_class',
    targetSelector: '.elm',
    property: 'data-exc',
  },
];
/**
 *
 */
class GpPSwatchesChildProductExportService extends GpChildProductExportService {
  /**
   * cloneAtom
   * @override GpChildProductExportService.cloneAtom
   * @param $atomClone :jQuery
   * @param templateTmp :string
   * @returns $atomClone
   */
  cloneAtom($atomClone: JQuery, templateTmp: string): JQuery {
    const matchedString = new RegExp('product.options', 'g');
    templateTmp = templateTmp.replace(matchedString, 'product.options_with_values');
    return $atomClone.html(templateTmp);
  }
}
/**
 * (P) Swatches Atom Module
 */
export class GpPSwatches extends GpChildProduct {
  public $el: JQuery;
  public atomServiceExportData: AtomExportInterface;
  /**
   * Thông tin riêng của Atom
   * @returns object watch
   */
  getDataAtom(): AtomModel {
    const data: AtomModel = {
      type: this.getType(),
      id: this.getId(),
      nodeId: this.getIdNode(),
      name: this.getLabel(),
      slug: this.getSlug(),
      groupName: this.getGroupName(),
      settings: this.getSettings(),
      formControlSettings: FormControlSettings,
      data: this.getDataChilds(),
      html: this.getTemplate(),
      class: this.getTargetClassSelector(), //".gf_swatch",
      style: this.getStyle(),
      script: this.getScript(),
      libs: this.getLibs(),
      fonts: [] as Array<Font>,
      version: 1,
    };
    return data;
  }

  /**
   * override getLabel từ Atom
   * @returns Name
   */
  getLabel(): string {
    return this.$el.attr('data-label') || '(P) Swatches';
  }
  /**
   * override getLabel từ Atom
   * @returns Slug
   */
  getSlug(): string {
    return this.$el.attr('data-slug') || 'p-swatches';
  }
  /**
   * GetModuleId
   * @returns number
   */
  getModuleId() {
    let moduleId = HELPER.MakeIdInsideList(this.$el);
    if (moduleId == '') {
      moduleId = this.$el.attr('data-id') || HELPER.MakeId().toString();
    }

    return moduleId;
  }

  /**
   * get Data from html attr
   * @returns Group Name
   */
  getGroupName(): string {
    return this.$el.attr('data-groupname') || 'Module';
  }
  /**
   * getFormControlSettings;
   * @returns setting read/write from left-sidebar to live page
   */
  getFormControlSettings(): Array<FormControlSetting> {
    return FormControlSettings;
  }

  /**
   * getTargetClass
   * @returns Class name of module
   */
  getTargetClassSelector(): string {
    return '.' + pSwatchesInfor.class;
  }
  /**
   * getTargetWrap
   * @returns Class name of module
   */
  getTargetWrapSelector(): string {
    return '.' + pSwatchesInfor.wrap;
  }

  /**
   * @override Atom.initAtomExport
   */
  initAtomExport() {
    this.atomServiceExportData = new GpPSwatchesChildProductExportService(this);
  }
}
